import React, { useEffect } from 'react';
import { Table, Badge, Pagination, Button } from 'react-bootstrap';
import { formatDistanceToNow } from 'date-fns';

const ExecutionTable = ({
  executions,
  title,
  onExecutionClick,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  sortOrder = "desc",
  onPageChange,
  onSortChange,
  showResults = true,
  showModule = false,
}) => {
  console.log(`[ExecutionTable] Rendering ${title} with ${executions?.length || 0} executions`);
  
  useEffect(() => {
    console.log(`[ExecutionTable] ${title} mounted/updated with executions:`, executions);
    return () => console.log(`[ExecutionTable] ${title} unmounting`);
  }, [executions, title]);
  return (
    <div className="mb-4">
      <h3>{title}</h3>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            <th>
              Start Time
              {onSortChange && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ms-1"
                  onClick={() => onSortChange(sortOrder === "desc" ? "asc" : "desc")}
                  title={`Sort ${sortOrder === "desc" ? "ascending" : "descending"}`}
                >
                  {sortOrder === "desc" ? "↓" : "↑"}
                </Button>
              )}
            </th>
            <th>Duration</th>
            <th>Status</th>
            {showModule && <th>Module</th>}
            <th>Selection Mode</th>
            <th>Execution Mode</th>
            {showResults && <th>Results</th>}
          </tr>
        </thead>
        <tbody>
          {executions.length === 0 ? (
            <tr>
              {/* <td colSpan={showResults ? "8" : "7"} className="text-center">No executions found</td> */}
              <td colSpan={7 + (showResults ? 1 : 0) + (showModule ? 1 : 0)} className="text-center">No executions found</td>
            </tr>
          ) : (
            executions.map((execution) => {
              console.debug(`[ExecutionTable] Processing execution ID ${execution.execution_id}:`, execution);
              // Convert UTC timestamp to local timezone
              const startDate = new Date(execution.timestamp_start + 'Z'); // Append Z to indicate UTC
              const duration = execution.timestamp_end
                ? new Date(execution.timestamp_end + 'Z') - startDate
                : new Date() - startDate;
              
              // Format duration in minutes and seconds
              const durationMinutes = Math.floor(duration / 60000);
              const durationSeconds = Math.floor((duration % 60000) / 1000);
              const durationFormatted = `${durationMinutes}m ${durationSeconds}s`;
              console.debug(`[ExecutionTable] Execution ID ${execution.execution_id} duration: ${durationFormatted} (${duration}ms)`);
              
              // Progress or status
              const status = execution.has_ended ? "Completed" : `Running`;
              console.debug(`[ExecutionTable] Execution ID ${execution.execution_id} status: ${status}`);
              
              const statusBadge = execution.has_ended ? (
                <Badge bg="success">Completed</Badge>
              ) : (
                <Badge bg="warning">Running</Badge>
              );

              // Selection mode badge with color coding
              const selectionModeBadge = execution.selection_mode ? (
                <Badge bg={execution.selection_mode === 'full' ? 'primary' : 'warning'}>
                  {execution.selection_mode}
                </Badge>
              ) : (
                <Badge bg="secondary">N/A</Badge>
              );

              const executionModeBadge = execution.execution_mode ? (
                <Badge bg={(execution.execution_mode === 'normal' || execution.execution_mode === 'full') ? 'primary' : 'secondary'}>
                  {execution.execution_mode}
                </Badge>
              ) : (
                <Badge bg="secondary">N/A</Badge>
              );
              
              return (
                <tr key={execution.execution_id} onClick={() => onExecutionClick && onExecutionClick(execution.execution_id)} style={{ cursor: onExecutionClick ? 'pointer' : 'default' }}>
                  <td>{execution.execution_id}</td>
                  <td>{startDate.toLocaleString()} ({formatDistanceToNow(startDate, { addSuffix: true })})</td>
                  <td>{durationFormatted}</td>
                  <td>{statusBadge}</td>
                  {showModule && <td>{execution.executed_module}</td>}
                  <td>{selectionModeBadge}</td>
                  <td>{executionModeBadge}</td>
                  {showResults && (
                    <td style={{ whiteSpace: "normal", wordBreak: "break-all" }}>
                      {execution.has_ended && execution.execution_outcome
                        ? JSON.stringify(execution.execution_outcome)
                        : "N/A"}
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
      
      {/* Compact Pagination Controls */}
      {onPageChange && (
        <div className="d-flex justify-content-between align-items-center mt-2">
          <small className="text-muted">
            Page {currentPage} of {totalPages} ({pageSize} per page)
          </small>
          {totalPages > 1 ? (
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              
              {/* Show current page and adjacent pages */}
              {currentPage > 2 && <Pagination.Ellipsis disabled />}
              {currentPage > 1 && (
                <Pagination.Item onClick={() => onPageChange(currentPage - 1)}>
                  {currentPage - 1}
                </Pagination.Item>
              )}
              <Pagination.Item active>{currentPage}</Pagination.Item>
              {currentPage < totalPages && (
                <Pagination.Item onClick={() => onPageChange(currentPage + 1)}>
                  {currentPage + 1}
                </Pagination.Item>
              )}
              {currentPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
              
              <Pagination.Next
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          ) : (
            <small className="text-muted">Single page</small>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutionTable;